import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Receipt, DownloadSimple, ShareNetwork, WhatsappLogo, EnvelopeSimple, Trash, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, BACKEND, fmtDate, downloadFile, waLink } from "@/lib/api";
import { PageHead } from "./Properties";
import { DocumentForm } from "./DocumentForm";
import { DocBranding } from "./DocBranding";

const link = (d) => `${BACKEND}/api/public/docs/${d.share_token}`;
const status = (d) => (d.kind === "receipt" || (d.total && d.amount_paid >= d.total) ? ["Paid", "bg-[#E3EEE2] text-[#3E6B3C]"] : d.amount_paid ? ["Part paid", "bg-[#FBEBD2] text-[#8A5A12]"] : ["Unpaid", "bg-[#F9DED4] text-[#A6431F]"]);
const msg = (d) => `Hi ${d.guest_name.split(" ")[0]}, here is your ${d.kind} ${d.number} for ${d.currency} ${Number(d.total).toLocaleString()}: ${link(d)}`;

async function share(d) {
  try {
    const r = await api.get(`/documents/${d.doc_id}/pdf`, { responseType: "blob" });
    const file = new File([r.data], `${d.number}.pdf`, { type: "application/pdf" });
    if (navigator.canShare?.({ files: [file] })) return await navigator.share({ files: [file], title: d.number, text: msg(d) });
    if (navigator.share) return await navigator.share({ title: d.number, text: msg(d), url: link(d) });
  } catch (e) { if (e?.name === "AbortError") return; }
  await navigator.clipboard.writeText(link(d)).catch(() => {});
  toast.success("Share link copied");
}

const Act = ({ onClick, href, icon: Icon, label, testId }) => {
  const cls = "grid h-9 w-9 place-items-center rounded-lg text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-foreground";
  return href ? <a href={href} target="_blank" rel="noreferrer" title={label} className={cls} data-testid={testId}><Icon size={18} /></a>
    : <button onClick={onClick} title={label} className={cls} data-testid={testId}><Icon size={18} /></button>;
};

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [props, setProps] = useState([]);
  const [guests, setGuests] = useState([]);
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(params.get("new"));
  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data));
    api.get("/properties").then((r) => setProps(r.data));
    api.get("/guests").then((r) => setGuests(r.data));
  }, []);
  const close = () => { setForm(null); if (params.get("new")) setParams({}); };
  const del = async (d) => {
    if (!window.confirm(`Delete ${d.number}? This can't be undone.`)) return;
    await api.delete(`/documents/${d.doc_id}`); setDocs((x) => x.filter((y) => y.doc_id !== d.doc_id)); toast.success(`${d.number} deleted`);
  };

  return (
    <div data-testid="documents-page">
      <PageHead eyebrow="Billing" title="Invoices & receipts">
        <div className="flex gap-2">
          <button onClick={() => setForm("invoice")} data-testid="new-invoice-button" className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#0E1729] px-4 font-semibold text-white"><FileText /> New invoice</button>
          <button onClick={() => setForm("receipt")} data-testid="new-receipt-button" className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#D96C4A] px-4 font-semibold text-white hover:bg-[#C25D3E]"><Receipt /> New receipt</button>
        </div>
      </PageHead>
      <DocBranding />
      {!props.length && docs && <p className="mb-4 rounded-xl bg-[#FBEBD2] p-4 text-sm">Add an Airbnb first, then you can create invoices and receipts for it.</p>}
      <div className="overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="documents-table">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">{["Number", "Guest", "Airbnb", "Total", "Status", "Date", ""].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-border">
            {docs && docs.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-muted-foreground" data-testid="documents-empty">No invoices or receipts yet. Create one in seconds and share it on WhatsApp.</td></tr>}
            {(docs || []).map((d) => {
              const [st, cls] = status(d);
              return (
                <tr key={d.doc_id} data-testid={`doc-row-${d.doc_id}`}>
                  <td className="px-5 py-3"><p className="font-mono font-semibold">{d.number}</p><p className="text-xs capitalize text-muted-foreground">{d.kind}</p></td>
                  <td className="px-5 py-3"><p className="font-semibold">{d.guest_name}</p><p className="text-xs text-muted-foreground">{d.guest_phone}</p></td>
                  <td className="px-5 py-3">{props.find((p) => p.property_id === d.property_id)?.data.name || "—"}</td>
                  <td className="px-5 py-3 font-semibold">{d.currency} {Number(d.total).toLocaleString()}</td>
                  <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{st}</span></td>
                  <td className="px-5 py-3 text-muted-foreground">{fmtDate(d.issue_date)}</td>
                  <td className="px-3 py-2"><div className="flex justify-end">
                    <Act icon={DownloadSimple} label="Download PDF" onClick={() => downloadFile(`/documents/${d.doc_id}/pdf`, `${d.number}.pdf`)} testId={`doc-download-${d.doc_id}`} />
                    <Act icon={ShareNetwork} label="Share" onClick={() => share(d)} testId={`doc-share-${d.doc_id}`} />
                    {d.guest_phone && <Act icon={WhatsappLogo} label="Send on WhatsApp" href={waLink(d.guest_phone, msg(d))} testId={`doc-whatsapp-${d.doc_id}`} />}
                    {d.guest_email && <Act icon={EnvelopeSimple} label="Email" href={`mailto:${d.guest_email}?subject=${encodeURIComponent(`${d.kind === "receipt" ? "Receipt" : "Invoice"} ${d.number}`)}&body=${encodeURIComponent(msg(d))}`} testId={`doc-email-${d.doc_id}`} />}
                    <Act icon={LinkSimple} label="Copy link" onClick={() => navigator.clipboard.writeText(link(d)).then(() => toast.success("Link copied"))} testId={`doc-copy-${d.doc_id}`} />
                    <Act icon={Trash} label="Delete" onClick={() => del(d)} testId={`doc-delete-${d.doc_id}`} />
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <DocumentForm open={!!form} kind={form || "invoice"} onClose={close} properties={props} guests={guests}
        onCreated={(d) => { setDocs((x) => [d, ...(x || [])]); close(); downloadFile(`/documents/${d.doc_id}/pdf`, `${d.number}.pdf`); }} />
    </div>
  );
}
